import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row, Form, Badge } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { useApi } from 'hooks/useApi';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { useNavigate } from 'hooks/useNavigate';
import CompanyDocumentsAdapter from './invoiceMonthlies.adapter';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import Helpers from 'helpers';
import moment from 'moment';
import { useFilter } from 'hooks/useFilter';
import { getPage } from 'hooks/useUser';
import InvoiceYears from 'libs/api/invoiceYears';

// const data = await api.confirm_replace(companyId);

const api = new InvoiceYears();

export const statusColors = {
  created: 'yellow',
  confirm: 'green',
  reject: 'red',
};
const periodTypeMap = {
  year: 'Anno',
  first_half: 'Primo Semestre',
  last_half: 'Secondo Semestre',
};

const useInvoiceMonthlies = (isMainPage = false) => {
  const { t } = useTranslation();
  const { ability } = useRecoilValue(getPage);
  const [isShowedPopup, setIsShowedPopup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [company, setCompany] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableHolidayData, setTableHolidayData] = useState(null);
  const [reportId, setReportId] = useState(null);
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const [periodType, setPeriodType] = useState(null);
  const { filterData } = useFilter();
  const additionalSingleParams = useMemo(() => {
    return { group_data: true };
  }, []);
  const {
    singleItem,
    loading,
    data,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    approveErrors,
    setApproveErrors,
  } = useApi({
    resourceKey: 'invoice_years',
    singleResourceKey: 'invoice_years',
    api: api,
    isMainPage,
    adapter: CompanyDocumentsAdapter,
    aditionalParamsForSingle: additionalSingleParams,
  });

  const statusTexts = useMemo(
    () => ({
      confirm: t('Confermato'),
      created: t('Nuovo'),
      reject: t('Rifiutato'),
    }),
    [],
  );
  const handleCompanySelect = async (companyId) => {
    try {
      const response = await api.getCompanyById(companyId);
      const invoiceYearType = response?.data?.company?.invoice_year_type;
      setPeriodType(invoiceYearType);
      if (invoiceYearType === 'year') {
        form.setFieldsValue({ period_type: 'year' });
      } else if (invoiceYearType === 'half_year') {
        form.setFieldsValue({ period_type: 'first_half' });
      }
    } catch (e) {
      UIHelper.openNotificationWithIcon(
        'error',
        e?.response?.data?.errors[0]?.messages[0],
      );
    }
  };
  useEffect(() => {
    const initialCompanyId = form.getFieldValue('company_id');
    if (initialCompanyId) {
      handleCompanySelect(initialCompanyId);
    }
  }, [form.getFieldValue('company_id')]);

  const handleCreate = useCallback(
    async (values, step) => {
      setUpdateInProcess(true);
      const formData = new FormData();
      const today = new Date();
      const periodYear = today.getFullYear() - 1;
      const period = `${periodYear}-01-01`;
      formData.append('period', period);
      Object.keys(values).forEach((key) => {
        if (key === 'document') {
          if (Array.isArray(values[key]) && values[key].length > 0) {
            formData.append(`${key}`, values[key][0]);
          }
        } else {
          formData.append(`${key}`, values[key]);
        }
      });
      try {
        if (step === 'stepOne') {
          const { data } = await api.create({
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          setCompany(data);
          setReportId(data.invoice_year?.id);
          if (data?.confirmation_for_replace_needed) {
            setShowModal(true);
            return setIsShowedPopup(true);
          }
          // return navigate(UIHelper.getListRoute(pathname, 1));
        } else if (step === 'stepTwo') {
          const data = await api.confirm_replace(company?.invoice_year?.id);
          if (!data || data?.errors?.length) {
            UIHelper.openNotificationWithIcon(
              'error',
              'Something went wrong, try again',
            );
            return false;
          }
          // navigate(UIHelper.getListRoute(pathname, 1));
          return data.data.success;
        } else {
          // return navigate(UIHelper.getListRoute(pathname, 1));
        }
      } catch (e) {
        const errors = e.response.data?.errors || [];
        UIHelper.openNotificationWithIcon(
          'error',
          e?.response?.data?.errors[0]?.messages[0],
        );
        form.setFields(
          ErrorHelper.getErrorsForForm(errors[0], {
            company: 'company_id',
          }),
        );
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, company, pathname, shoppingCenters],
  );
  const handleCreateOrUpdate = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === 'file') {
          if (values[key]) {
            formData.append(`${key}`, values[key][0]?.originFileObj || '');
          }
        } else if (key === 'period') {
          formData.append(key, new Date());
        } else {
          formData.append(`${key}`, values[key]);
        }
      });
      try {
        if (!id) {
          await api.create({
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname, 1));
        } else {
          await api.put(id, {
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response.data?.errors || [];

        form.setFields(
          ErrorHelper.getErrorsForForm(errors[0], {
            company: 'company_id',
          }),
        );
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id, shoppingCenters],
  );

  const handleUploadExcelFile = useCallback(
    async (excelFile) => {
      const formData = new FormData();
      formData.append('file', excelFile);
      try {
        if (reportId) {
          const response = await api.put(reportId, {
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          if (response?.data?.success) {
            handleFetchData(reportId);
          } else {
            UIHelper.openNotificationWithIcon(
              'error',
              'Failed to upload Excel file',
            );
          }
        }
      } catch (e) {
        UIHelper.openNotificationWithIcon(
          'error',
          'Error uploading Excel file',
        );
      }
    },
    [reportId],
  );
  const handleDownloadExcel = async () => {
    try {
      const response = await api.getExcelSingle(id);
      Helpers.downloadFile(response, 'invoice.xlsx');
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    }
  };

  const handleFetchData = async (reportId) => {
    try {
      const responseRegular = await api.get(`${reportId}`);
      const regularData = responseRegular.data.data_month;

      const responseHoliday = await api.get(`/${reportId}?only_holiday=true`);
      const holidayData = responseHoliday.data.data_month;

      setTableData(regularData);
      setTableHolidayData(holidayData);
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    }
  };

  const onClickFile = useCallback(async (filePath) => {
    const fullPath = Helpers.getFilePath(filePath);
    window.open(fullPath, '_blank');
  }, []);
  const columns = useMemo(() => {
    return [
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
      },
      {
        title: t('Fatturato del'),
        dataIndex: 'period_type',
        render: (text, record) => {
          const year = record.period?.split(' ')?.[2] || '';
          return `${periodTypeMap[text] || text} ${year}`;
        },
        sorter: () => {},
      },
      {
        title: t('Data caricamento'),
        dataIndex: 'created_at',
        sorter: () => {},
        render: (element) => {
          return moment(element).format('DD/MM/YYYY');
        },
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => {},
        render: (data) => (
          <Badge color={statusColors[data]} text={statusTexts[data]} />
        ),
      },
      {
        title: t('Scarica file'),
        dataIndex: 'file',
        render: (data) =>
          data && (
            <Button
              htmlFor='download-file'
              type='primary-white'
              icon={<DownloadOutlined style={{ marginRight: '10px' }} />}
            >
              <a
                id='download-file'
                target='_blank'
                rel='noopener noreferrer'
                href={data?.url}
              >
                {t('Scarica File')}
              </a>
            </Button>
          ),
      },
      {
        title: t('Scarica file PDF'),
        dataIndex: 'document',
        render: (data) =>
          data && (
            <Button
              htmlFor='download-document'
              type='primary-white'
              icon={<DownloadOutlined style={{ marginRight: '10px' }} />}
            >
              <a
                id='download-document'
                target='_blank'
                rel='noopener noreferrer'
                href={data?.url}
                download
              >
                {t('Scarica File PDF')}
              </a>
            </Button>
          ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 100,
        fixed: 'right',
        render: (data, row) => {
          return (
            <Row
              gutter={16}
              justify={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              {row?.can_update !== false && ability?.can_update && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {/*{row.can_destroy !== false && (*/}
              {/*  <Col>*/}
              {/*    <Link to={`${pathname}/${row.id}/delete`}>*/}
              {/*      <Button type={'red'} icon={<DeleteIcon />} />*/}
              {/*    </Link>*/}
              {/*  </Col>*/}
              {/*)}*/}
            </Row>
          );
        },
      },
    ];
  }, [pathname, t, onClickFile, ability, statusTexts]);

  useEffect(() => {
    if (id && !isMainPage && singleItem?.id) {
      form.setFields([
        {
          name: 'period',
          value:
            singleItem.periodNormal &&
            moment(singleItem.periodNormal).format('DD MMMM YYYY HH:mm'),
        },
        {
          name: 'file',
          value: null,
        },
        {
          name: 'company_id',
          value: singleItem.company?.id,
        },
        {
          name: 'period_type',
          value: singleItem.company?.period_type,
        },
      ]);
    }
  }, [singleItem, form, id, isMainPage]);

  const handleApprove = async (values, paramKey, backCount) => {
    const data = {
      comment: values.description,
      company_id: singleItem?.company?.id,
      by_month: singleItem?.period,
    };
    switch (values.status) {
      case 'Approva': {
        data.status = 'confirm';
        break;
      }
      case 'Rifiutato': {
        data.status = 'reject';
        break;
      }
      default:
        break;
    }
    setUpdateInProcess(true);
    try {
      await api.approve(id, data);
      navigate(UIHelper.getListRoute(pathname, backCount));
    } catch (e) {
      setApproveErrors(
        (e?.response?.data?.errors &&
          e?.response?.data?.errors[0] &&
          e?.response?.data?.errors[0]?.messages) ||
          [],
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  const handleDownloadPdf = useCallback(
    async (url, fileName = 'vacancy_application.pdf') => {
      setUpdateInProcess(true);
      try {
        const response = await api.getPDF(url);
        Helpers.downloadFile(response, fileName);
      } catch (e) {
        UIHelper.serverError();
      } finally {
        setUpdateInProcess(false);
      }
    },
    [],
  );

  const filters = useMemo(
    () => [
      {
        title: t('Insegna'),
        type: 'select',
        name: 'company_id',
        values: filterData?.companies?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
        inactiveValues: filterData?.inactive_companies?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Village'),
        type: 'select',
        name: 'shopping_center_id',
        values: filterData?.shopping_centers?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Date'),
        type: 'range',
        picker: 'month',
        name: 'by_month',
        keys: ['from', 'to'],
      },
      {
        title: t('Stato'),
        type: 'select',
        name: 'by_status',
        values: Object.entries(statusTexts)?.map(([key, value]) => ({
          value: key,
          title: value,
        })),
      },
    ],
    [t, shoppingCenters, filterData, statusTexts],
  );

  return {
    columns,
    singleItem,
    form,
    data,
    loading,
    filters,
    updateInProcess,
    onChangeTableFilter,
    handleCreateOrUpdate,
    handleCreate,
    shops: filterData?.companies,
    inactiveShops: filterData?.inactive_companies || [],
    handleApprove,
    approveErrors,
    handleDownloadPdf,
    statusColors,
    statusTexts,
    isShowedPopup,
    setIsShowedPopup,
    showModal,
    setShowModal,
    handleUploadExcelFile,
    handleDownloadExcel,
    reportId,
    tableData,
    tableHolidayData,
    handleCompanySelect,
    periodType,
  };
};

export default useInvoiceMonthlies;
